import { useEffect, useState } from "react";
import { Star, Check, PenLine } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onChange, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type={onChange ? "button" : undefined}
        disabled={!onChange}
        onClick={onChange ? () => onChange(n) : undefined}
        data-testid={onChange ? `${testid}-${n}` : undefined}
        className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
        aria-label={`${n} star`}
      >
        <Star size={onChange ? 22 : 14} className={n <= value ? "fill-petal text-petal" : "text-pine/25"} />
      </button>
    ))}
  </div>
);

const ReviewCard = ({ r }) => (
  <article data-testid={`review-${r.id}`} className="bg-blush rounded-2xl border border-petal/15 p-5 lg:p-6">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h4 className="font-serif font-semibold text-ink text-lg leading-snug">{r.title}</h4>
        <p className="mt-1 text-xs text-sage">
          <span className="font-semibold text-pine">{r.name}</span> · {r.city}{r.years_lived ? ` · ${r.years_lived}` : ""}
        </p>
      </div>
      <Stars value={r.rating} />
    </div>
    <p className="mt-3 text-sm text-ink/85 leading-relaxed whitespace-pre-line">{r.body}</p>
  </article>
);

const empty = { city: "", name: "", email: "", rating: 0, years_lived: "", title: "", body: "" };

export const CommunityReviews = ({ country }) => {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    fetch(`${API}/reviews?country=${country.slug}`).then((r) => r.json()).then(setReviews).catch(() => setReviews([]));
  }, [country.slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setError("Please tap a star rating.");
    if (form.body.trim().length < 40) return setError("Give us at least a few sentences (40+ characters) — sisters want the real details.");
    setError("");
    setState("loading");
    try {
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, country_slug: country.slug }),
      });
      if (!res.ok) throw new Error("failed");
      setState("done");
    } catch {
      setState("idle");
      setError("Something went wrong, sis — please try again.");
    }
  };

  const input = "w-full bg-white border border-pine/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-petal/40";

  return (
    <div data-testid="community-reviews" className="grid grid-cols-1 lg:grid-cols-12 gap-10">
      <div className="lg:col-span-6">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-petal">Mom/Sister reviews · {reviews.length}</p>
        <h3 className="mt-3 font-serif font-semibold text-ink text-2xl lg:text-3xl leading-tight">Sisters who've actually lived in {country.name}.</h3>
        <p className="mt-3 text-sm text-sage leading-relaxed">Real reviews from real mothers. Every submission is read by Hijrah Mom before it's published — no bots, no agencies, no sponsored praise.</p>
        <div className="mt-8 space-y-4" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <div data-testid="reviews-empty" className="border border-dashed border-pine/25 rounded-2xl p-8 text-center text-sm text-sage">
              No published reviews yet. Lived here? Be the first sister to share.
            </div>
          ) : (
            reviews.map((r) => <ReviewCard key={r.id} r={r} />)
          )}
        </div>
      </div>

      <div className="lg:col-span-6">
        <div className="bg-cream rounded-3xl border border-pine/10 p-6 lg:p-8">
          {state === "done" ? (
            <div data-testid="review-success" className="text-center py-10">
              <span className="inline-flex w-14 h-14 rounded-full bg-pine text-cream items-center justify-center"><Check size={24} /></span>
              <h4 className="mt-5 font-serif font-semibold text-ink text-xl">JazakAllahu khair — it's in the queue.</h4>
              <p className="mt-2 text-sm text-sage max-w-sm mx-auto">Hijrah Mom reads every review before it goes live. Yours will appear here once approved, insha'Allah.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="review-form">
              <div className="flex items-center gap-2 text-pine"><PenLine size={16} /><h4 className="font-serif font-semibold text-xl">Share your {country.name} review</h4></div>
              <div>
                <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-pine">Your rating</span>
                <div className="mt-2"><Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} testid="review-rating" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="review-city-input" required value={form.city} onChange={set("city")} className={input} placeholder="City you lived in" />
                <input data-testid="review-years-input" value={form.years_lived} onChange={set("years_lived")} className={input} placeholder="How long? e.g. 2 years" />
                <input data-testid="review-name-input" required value={form.name} onChange={set("name")} className={input} placeholder="Name shown publicly (e.g. Umm Zayd)" />
                <input data-testid="review-email-input" type="email" required value={form.email} onChange={set("email")} className={input} placeholder="Email (never shown)" />
              </div>
              <input data-testid="review-title-input" required minLength={3} value={form.title} onChange={set("title")} className={input} placeholder="Headline — 'Kid-friendly but pricey'" />
              <textarea data-testid="review-body-input" required rows={5} value={form.body} onChange={set("body")} className={`${input} resize-y`} placeholder="Schools, rent, safety, community, what surprised you, what you'd warn a sister about..." />
              {error && <p data-testid="review-error" className="text-sm text-petal-dark font-medium">{error}</p>}
              <button data-testid="review-submit-btn" type="submit" disabled={state === "loading"} className="inline-flex items-center gap-2 bg-petal text-white text-sm font-semibold px-6 py-3 rounded-full shadow-md shadow-petal/25 hover:bg-pine transition-colors disabled:opacity-60">
                {state === "loading" ? "Submitting..." : "Submit for review"}
              </button>
              <p className="text-[11px] text-sage-muted leading-relaxed">By submitting you agree your review may be published (with your display name only) once approved.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
